/**
 * Area Landing Page Component — Restore.Dental
 * 
 * SEO-optimized neighborhood pages targeting:
 * - "dentist {area}"
 * - "dental implants {area}"
 * - "cosmetic dentist {area}"
 * 
 * Each page emits: Dentist + BreadcrumbList + Speakable JSON-LD
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, MapPin, Clock, Train, CheckCircle2, Award, Anchor, Smile, Wrench, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getAreaBySlug } from '../data/areas';
import { getLocalHub } from '../data/local';
import { PUBLIC_SITE_URL } from '../data/seo';
import SiteFooter from './SiteFooter';
import Breadcrumbs from './Breadcrumbs';
import PageHeader from './PageHeader';
import { 
  getPersonClinicianLD, 
  getDentistClinicLD,
  getBreadcrumbLD,
  getSpeakableLD 
} from '../data/structuredData';

const AreaPage = () => {
  const location = useLocation();
  
  // Extract slug from path: /dentist-ealing → ealing
  const slug = location.pathname.replace('/dentist-', '');
  const area = getAreaBySlug(slug);
  // Unique, landmark-specific copy for this neighbourhood (data/local/<area>.js)
  const hub = getLocalHub(slug);

  // Handle 404 if area not found
  if (!area) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-rabbit mb-4">Area Not Found</h1>
          <Link to="/" className="text-sky-gray hover:underline">
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Services data
  const services = [
    {
      title: 'Dental Implants',
      description: 'Permanent tooth replacement that looks and functions like natural teeth. Restore confidence and eating function.',
      Icon: Anchor,
      link: '/implants-acton'
    },
    {
      title: 'Invisalign',
      description: 'Discreet teeth straightening for adults and teenagers. 150+ cases completed by Dr. Patel.',
      Icon: Smile,
      link: '/invisalign-acton'
    },
    {
      title: 'Restorative Dentistry',
      description: 'Fix broken, damaged, or missing teeth. Crowns, bridges, and composite bonding that restores function.',
      Icon: Wrench,
      link: '/dentist-acton'
    },
    {
      title: 'Composite Bonding',
      description: 'Same-day smile transformation. Fix chips, gaps, and discoloration with natural-looking results.',
      Icon: Sparkles,
      link: '/composite-bonding-acton'
    }
  ];

  return (
    <>
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={hub.title}
        description={hub.description}
        path={`/dentist-${area.slug}`}
        keywords={area.seo.keywords.split(', ')}
        structuredData={[
          getDentistClinicLD(),
          getPersonClinicianLD(),
          getBreadcrumbLD([
            { name: 'Home', url: `${PUBLIC_SITE_URL}/` },
            { name: `Dentist ${area.name}`, url: `${PUBLIC_SITE_URL}/dentist-${area.slug}` },
          ]),
          getSpeakableLD([
            `Dentist near ${area.name} ${area.postcode}`,
            `Expert dental care ${area.distanceMinutes} minutes from ${area.tubes[0]}`,
            `Dr. Kartik Patel - dental implants, Invisalign, restorative dentistry`,
          ]),
        ]}
      />

      <div className="min-h-screen bg-white">
        {/* Header */}
        <PageHeader />

        {/* Hero Section */}
        <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-sky-gray/5 to-white">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left: Content */}
              <div>
                <div className="mb-4">
                  <Breadcrumbs items={[{ name: 'Home', to: '/' }, { name: 'Areas we serve', to: '/contact' }, { name: `Dentist ${area.name}` }]} />
                </div>
                <div className="inline-flex items-center gap-2 bg-sky-gray/10 px-4 py-2 rounded-full mb-6">
                  <MapPin className="w-4 h-4 text-sky-gray" />
                  <span className="text-sm font-medium text-burnt-coral">
                    Serving {area.name} {area.postcode}
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold text-rabbit mb-6 leading-tight">
                  Private Dentist Near<br />
                  <span className="text-sky-gray">{area.name} {area.postcode}</span>
                </h1>

                <div className="direct-answer bg-white border-2 border-[#01A9B4]/30 rounded-2xl p-5 mb-8 shadow-md" data-testid="direct-answer">
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#006A71] mb-2">Quick answer</p>
                  <p className="text-base md:text-lg text-chocolate-malt leading-relaxed" data-testid="area-quick-answer">
                    {hub.quickAnswer}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <a href="tel:07787814064">
                    <Button size="lg" className="w-full sm:w-auto bg-sky-gray hover:bg-burnt-coral">
                      <Phone className="w-5 h-5 mr-2" />
                      Call Now
                    </Button>
                  </a>
                  <Link to="/">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      View All Services
                    </Button>
                  </Link>
                </div>

                {/* Trust indicators */}
                <div className="flex flex-wrap gap-6 text-sm text-chocolate-malt">
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-amberlight" />
                    <span>4.9★ Google Rating</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-sky-gray" />
                    <span>0% Finance Available</span>
                  </div>
                </div>
              </div>

              {/* Right: Area Facts Card */}
              <Card className="border-2 border-sky-gray/20 shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-rabbit mb-6">
                    Getting to Restore.Dental
                  </h3>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-sky-gray/10 flex items-center justify-center flex-shrink-0">
                        <Train className="w-6 h-6 text-sky-gray" />
                      </div>
                      <div>
                        <div className="font-semibold text-rabbit mb-1">Nearest Stations</div>
                        <div className="text-chocolate-malt">{area.tubes.join(' • ')}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-amberlight/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-amberlight" />
                      </div>
                      <div>
                        <div className="font-semibold text-rabbit mb-1">Journey Time</div>
                        <div className="text-chocolate-malt">{area.distanceMinutes} minutes from {area.name}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-burnt-coral/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-burnt-coral" />
                      </div>
                      <div>
                        <div className="font-semibold text-rabbit mb-1">Clinic Location</div>
                        <div className="text-chocolate-malt">Acton, West London W3</div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-sky-gray/20">
                      <div className="text-sm text-chocolate-malt">
                        <strong>Who we see from {area.name}:</strong> {area.audience}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Local Hook Section */}
        <section className="py-16 px-4 bg-white">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-sky-gray/5 rounded-2xl p-8 md:p-12 border border-sky-gray/10">
              <h2 className="text-3xl font-bold text-rabbit mb-6">
                Why {area.name} Patients Choose Restore.Dental
              </h2>
              <p className="text-lg text-chocolate-malt leading-relaxed" data-testid="area-local-hook">
                {hub.localHook}
              </p>
            </div>
          </div>
        </section>

        {/* The bit that only makes sense for this neighbourhood */}
        <section className="pb-16 px-4 bg-white" data-testid="area-local-section">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-4">
              {hub.section.heading}
            </h2>
            <p className="text-chocolate-malt leading-relaxed">
              {hub.section.body}
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 px-4 bg-gradient-to-br from-white to-sky-gray/5">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
                Our Most Popular Treatments
              </h2>
              <p className="text-lg text-chocolate-malt max-w-2xl mx-auto">
                From dental implants to Invisalign, we solve problems with empathy and precision
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="border border-sky-gray/20 hover:border-sky-gray hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-sky-gray/10 flex items-center justify-center flex-shrink-0">
                        <service.Icon className="w-6 h-6 text-[#01A9B4]" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-rabbit mb-2">{service.title}</h3>
                        <p className="text-chocolate-malt mb-4">{service.description}</p>
                        <Link to={service.link} className="text-sky-gray hover:text-burnt-coral font-semibold inline-flex items-center gap-2">
                          Learn More →
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Meet Dr. Patel Section */}
        <section className="py-16 px-4 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="grid md:grid-cols-3 gap-8 items-start">
              {/* Portrait */}
              <div className="md:col-span-1">
                <div className="relative">
                  <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-sky-gray/20 to-burnt-coral/20">
                    <img
                      src="/dr-kartik-patel.webp"
                      alt="Dr Kartik Patel BDS, principal dentist at Restore.Dental, Acton"
                      width="720"
                      height="675"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto"
                      data-testid="area-dr-patel-photo"
                    />
                  </div>
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white px-6 py-2 rounded-full border-2 border-sky-gray/20 shadow-lg">
                    <div className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-amberlight" />
                      <span className="font-semibold text-rabbit">15+ Years</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="md:col-span-2">
                <h2 className="text-3xl font-bold text-rabbit mb-4">
                  Meet Dr. Kartik Patel
                </h2>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="px-3 py-1 bg-sky-gray/10 text-sky-gray rounded-full text-sm font-medium">
                    BDS (London)
                  </span>
                  <span className="px-3 py-1 bg-amberlight/10 text-amberlight rounded-full text-sm font-medium">
                    150+ Invisalign Cases
                  </span>
                  <span className="px-3 py-1 bg-burnt-coral/10 text-burnt-coral rounded-full text-sm font-medium">
                    Implant Certified
                  </span>
                  <span className="px-3 py-1 bg-sky-gray/10 text-sky-gray rounded-full text-sm font-medium">
                    Sedation Dentistry
                  </span>
                </div>

                <div className="bg-gradient-to-br from-sky-gray/5 to-white p-6 rounded-xl border border-sky-gray/10">
                  <h3 className="text-xl font-bold text-rabbit mb-4">
                    A Note on {area.name} Patients
                  </h3>
                  <p className="text-chocolate-malt leading-relaxed" data-testid="area-proof">
                    {hub.proofParagraph}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-gradient-to-br from-sky-gray to-burnt-coral text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Restore Your Smile?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Book a consultation with Dr. Kartik Patel — {area.distanceMinutes} minutes from {area.name}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:07787814064">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 07787 814 064
                </Button>
              </a>
              <Link to="/">
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border-white/30">
                  View All Services
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <SiteFooter />
      </div>
    </>
  );
};

export default AreaPage;
